import { useEffect, useRef, useState } from 'react';

/**
 * Shared open/dismiss mechanics for the dialog's comboboxes — ONE behavior for
 * both fields. Closes on any pointerdown outside `ref`; a suppress ref swallows
 * the label-forwarded click that immediately follows (the flicker-reopen race
 * between <label htmlFor> and blur/outside-close).
 */
export function useComboDismiss(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [open, setOpen] = useState(false);
  const suppressRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        suppressRef.current = true;
        // Reset after this event loop turn — the forwarded click (if any) has fired by then.
        setTimeout(() => {
          suppressRef.current = false;
        }, 0);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, containerRef]);
  const tryOpen = () => {
    if (suppressRef.current) {
      suppressRef.current = false;
      return;
    }
    setOpen(true);
  };
  const toggle = () => {
    if (open) setOpen(false);
    else tryOpen();
  };
  return { open, setOpen, tryOpen, toggle };
}
